import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { isHistoryDoorVisible } from "@/hooks/metricsHistoryDoorLogic";

/**
 * Remote on/off switch for the "ההתקדמות שלי" row in MetricsSection — a fake
 * door until 2026-09-09, the real history chooser since.
 *
 * Reads `app_config.enabled` for `metrics_history_door`. The default when the
 * row is missing or the read fails is VISIBLE — deliberately the opposite of
 * every other flag hook here; the reasoning lives in `metricsHistoryDoorLogic`.
 *
 * Not platform-split (unlike `livid_enabled_*` / `funnel_enabled_*`): the door
 * is one measurement across web and native, and splitting it would split the
 * sample for no operational gain.
 */
export function useMetricsHistoryDoor(): boolean {
  const { data } = useQuery<boolean | null>({
    queryKey: ["metrics-history-door"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("app_config")
        .select("enabled")
        .eq("key", "metrics_history_door")
        .maybeSingle();
      // An error and an absent row both mean "config has not told us to hide
      // it". Returning null rather than throwing keeps react-query from
      // retrying into a loop against a table that answers fine for everyone
      // else.
      if (error) return null;
      return data?.enabled ?? null;
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    retry: 1,
  });
  return isHistoryDoorVisible({ enabled: data });
}
